import React from 'react';
import PropTypes from 'prop-types';

import Form from '@ott/form';
import ToursGeoAutocomplete, { locators as geoLocators } from './blocks/ToursGeoAutocomplete';
import ToursNights from './blocks/ToursNights';
import TravellersWrap from './blocks/TravellersWrap/TravellersWrap';
import Datepicker from '@ott/datepicker';

import noop from 'lodash/noop';
import get from 'lodash/get';
import find from 'lodash/find';

import addDays from 'date-fns/addDays';

const suggestPlaceholders = {
  destination: 'Страна, город',
  departure: 'Откуда',
};

// todo move to constants
const MAX_DAYS_IN_RANGE = 16; // level.travel ограничивает кол-во ночей до 15-ти

class ToursSearchFormBase extends Form {
  static propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    departureSuggestions: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    destinationSuggestions: PropTypes.array,
    departureIsLoading: PropTypes.bool,
    destinationIsLoading: PropTypes.bool,

    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),

    // disable кнопку submit'а, когда значения инпутов не изменились
    disabledForSameSearchParams: PropTypes.bool, // todo сравнить со следующим

    formValues: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    isPristine: PropTypes.bool,
    formErrors: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    deviceInfo: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),

    scrollUpOnFocus: PropTypes.bool,

    // блокирует все контролы, но не кнопку сабмита // todo зачем
    disabled: PropTypes.bool, // todo naming

    onSubmit: PropTypes.func,

    getDepartureCities: PropTypes.func,
    getDestinationCountries: PropTypes.func,
    setDestinationQuery: PropTypes.func,
    setDepartureQuery: PropTypes.func,
    getDestinationSuggestions: PropTypes.func,

    /** Кейс: форма открыта на оверлее > открытие календаря > клик по оверлею >>> должны закрывать только календарь, а форма должна остаться */
    /** Управление закрытием в родительском контейнере */
    onFieldFocus: PropTypes.func,
    onFieldBlur: PropTypes.func,
  };

  static defaultProps = {
    initialValues: {},
    departureSuggestions: [],
    destinationSuggestions: [],
    departureIsLoading: false,
    destinationIsLoading: false,

    getDepartureCities: noop,
    getDestinationCountries: noop,
    setDestinationQuery: noop,
    setDepartureQuery: noop,
    getDestinationSuggestions: noop,

    onSubmit: noop,

    onFieldFocus: noop,
    onFieldBlur: noop,

    deviceInfo: {},
    scrollUpOnFocus: false,
  };

  state = {
    formTooltipType: null,
    departureInitialItem: null,
    destinationInitialItem: null,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleSubmit = this.handleSubmit.bind(this);

    this.onTextHandlers = {
      destination: props.setDestinationQuery,
      departure: props.setDepartureQuery,
    };
  }

  componentDidMount() {
    const {
      getDestinationCountries,
      getDestinationSuggestions,
      getDepartureCities,
      setDestinationQuery,
      initialValues: { departure, destination, destinationQuery },
      change,
    } = this.props;

    const processDeparture = (departureInitialItem) => {
      if (!departureInitialItem) {
        change('departure', null);
      }

      this.setState({
        departureInitialItem,
      });

      return departureInitialItem;
    };

    // eslint-disable-next-line no-unused-vars
    const departurePromise = getDepartureCities().then((departureCities) => {
      let departureInitialItem = null;
      if (departureCities instanceof Array) {
        departureInitialItem = find(departureCities, (city) => city && city.id === departure);
      }

      return processDeparture(departureInitialItem);
    });

    const processDestination = (destinationInitialItem) => {
      if (destinationInitialItem) {
        setDestinationQuery(destinationQuery || '', { isSimplify: true });
      } else {
        change('destination', null);
        setDestinationQuery('');
      }

      this.setState({
        destinationInitialItem,
      });

      return destinationInitialItem;
    };

    // eslint-disable-next-line no-unused-vars
    const destinationPromise = getDestinationCountries().then((desinationCountries) => {
      let destinationInitialItem = null;
      if (desinationCountries instanceof Array) {
        destinationInitialItem = find(
          desinationCountries,
          (el) => el && [el.id, el.code].includes(destination)
        );
      }
      if (!destinationInitialItem && destinationQuery) {
        return getDestinationSuggestions(destinationQuery).then((suggestions) => {
          destinationInitialItem = find(suggestions, (suggestion) => suggestion.id === destination);

          return processDestination(destinationInitialItem);
        });
      }

      return processDestination(destinationInitialItem);
    });
  }

  /* Autocomplete */

  handleDirectionSelect = (item, autocompleteType) => {
    if (!item) {
      return;
    }

    const { formValues } = this.props;

    const currentValue = get(formValues, autocompleteType, {});

    const hasNewValue = !!(item && item.id);

    if (hasNewValue) {
      const isSameValue = currentValue === item.id;

      if (!isSameValue) {
        this.props.change(autocompleteType, item.id);
        // eslint-disable-next-line no-underscore-dangle
        this._focusNextControl = true;
      }
    } else {
      this.props.change(autocompleteType, null);
    }

    this.onTextHandlers[autocompleteType](item.name, { isSimplify: true });
  };

  getGeoFocusHandler(type) {
    const { onFieldFocus } = this.props;

    return () => onFieldFocus(type);
  }

  getGeoBlurHandler(type) {
    const { onFieldBlur } = this.props;

    return () => onFieldBlur(type);
  }

  handleDepartureSelect = (item) => this.handleDirectionSelect(item, 'departure');

  handleDestinationSelect = (item) => this.handleDirectionSelect(item, 'destination');

  getGeoAutocompleteProps(type) {
    const { deviceInfo, scrollUpOnFocus } = this.props;

    const isDeparture = type === 'departure';

    return {
      type: 'text',
      theme: 'packagesSearchForm',
      valueKey: 'text',
      showExtraData: false,
      items: this.props[`${type}Suggestions`],
      initialActiveItem: this.state[`${type}InitialItem`],
      isLoading: this.props[`${type}IsLoading`],
      onText: this.onTextHandlers[type],
      onOptionSelect: isDeparture ? this.handleDepartureSelect : this.handleDestinationSelect,
      placeholder: suggestPlaceholders[type],
      isMobile: deviceInfo.isMobile,
      scrollUpOnFocus,
      isDeparture,
      placement: isDeparture ? 'right' : 'left',
      specificLocators: isDeparture ? geoLocators.departure : geoLocators.destination,
    };
  }

  getGeoAutocomplete = ({ input, meta, ...staticProps }) => {
    return (
      <ToursGeoAutocomplete
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...input}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...meta}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...staticProps}
        value={this.props[`${input.name}Query`]}
        valid={!(meta.submitFailed && meta.error)}
      />
    );
  };

  /* Datepicker */

  handleDateSelect = (startDate) => {
    const { change, deviceInfo } = this.props;

    change('dates', {
      startDate,
    });

    if (deviceInfo.isMobile) {
      // eslint-disable-next-line no-underscore-dangle
      this._focusNextControl = true;
    }
  };

  getDatepicker = ({ dates, isLoading, ...staticProps }) => {
    return (
      <Datepicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...dates}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...staticProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.getDatepickerProps()}
        isLoading={isLoading}
      />
    );
  };

  handleDatepickerFocus = () => {
    const { onFieldFocus } = this.props;
    onFieldFocus('datepicker');
  };

  handleDatepickerBlur = () => {
    const { onFieldBlur } = this.props;
    onFieldBlur('datepicker');
  };

  getDatepickerProps() {
    const { disabled, minDate, maxDate } = this.props;

    return {
      minDate,
      maxDate,
      maxDays: MAX_DAYS_IN_RANGE,
      endOnlyAfterStart: true,
      showNights: false,
      isDisabled: disabled,
      errorPlacement: 'top',
      placeholder: 'Когда',
      endDatePlaceholder: 'Обратно',
      onDateSelect: this.handleDateSelect,
      dayTooltipIsAvailable: true,
      getDayTooltipContent: this.getDayTooltipContent,
      calendarPlacement: 'left',
      pickOneDate: true,
      forceLongMonth: true,
      closeAfterSelect: true,
      onFocus: this.handleDatepickerFocus,
      onBlur: this.handleDatepickerBlur,
      withArrowIcon: true,
    };
  }

  /* Nights */

  formatEndDate = Intl.DateTimeFormat('ru', {
    day: 'numeric',
    month: 'long',
  }).format;

  getEndDateLabel(nights, flexNights, startDate) {
    const endDate = addDays(startDate, nights.input.value);

    return (
      endDate &&
      // eslint-disable-next-line no-restricted-globals
      !Number.isNaN(endDate) && (
        <span>
          {this.formatEndDate(endDate)}
          {flexNights.input.value && ' ±2'}
        </span>
      )
    );
  }

  getNights = ({ nights, flexNights, startDate, ...staticProps }) => {
    return (
      <ToursNights
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...staticProps}
        nightsField={nights}
        flexNightsField={flexNights}
        endDateLabel={this.getEndDateLabel(nights, flexNights, startDate)}
        withArrowIcon={true}
        hasDoneButton={true}
      />
    );
  };

  /* Travellers */

  handleTravellersFocus = () => {
    const { onFieldFocus } = this.props;
    onFieldFocus('travellers');
  };

  handleTravellersBlur = () => {
    const { onFieldBlur } = this.props;
    onFieldBlur('travellers');
  };

  getTravellers = ({ adults, childrenAges, ...staticProps }) => {
    // eslint-disable-next-line no-unused-vars
    const { disabled } = this.props;

    // todo check isDisabled
    return (
      <TravellersWrap
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...staticProps}
        adultsField={adults}
        childrenField={childrenAges}
        onFocus={this.handleTravellersFocus}
        onBlur={this.handleTravellersBlur}
      />
    );
  };

  /* Stuff */

  getFormProps() {
    return {
      onSubmit: this.handleSubmit,
      autoComplete: 'off',
      name: this.props.form,
    };
  }

  getErrors() {
    // todo check usage
    return this.props.validate(this.props.formValues);
  }

  isDisabledSubmitting() {
    return this.props.disabledForSameSearchParams && this.props.isPristine;
  }

  render() {
    return null;
  }
}

export default ToursSearchFormBase;
